import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import WorkspaceLoading from "../components/WorkspaceLoading";
import { useToastOnServiceError } from "../hooks/useToastOnServiceError";
import { cx } from "../utils/cx";

const SIDEBAR_STORAGE_KEY = "grozykart-admin-sidebar-expanded";
const DESKTOP_BREAKPOINT = 1100;

function getInitialSidebarState() {
  if (typeof window === "undefined") {
    return true;
  }

  const storedValue = window.localStorage.getItem(SIDEBAR_STORAGE_KEY);
  if (storedValue === "true") {
    return true;
  }
  if (storedValue === "false") {
    return false;
  }

  return window.innerWidth >= DESKTOP_BREAKPOINT;
}

function SidebarRevealButton({ onToggle }) {
  return (
    <button
      aria-label="Open navigation"
      aria-controls="admin-sidebar"
      aria-expanded={false}
      className="sidebar-reveal-button"
      type="button"
      onClick={onToggle}
    >
      <ChevronRight size={16} />
    </button>
  );
}

function AdminLayout() {
  const [desktopSidebarExpanded, setDesktopSidebarExpanded] = useState(
    getInitialSidebarState
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.innerWidth >= DESKTOP_BREAKPOINT;
  });
  const { status, hydrated, error } = useSelector((state) => state.admin);

  useToastOnServiceError(error, {
    title: "Workspace load failed",
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(desktopSidebarExpanded));
  }, [desktopSidebarExpanded]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handleChange = (event) => {
      setIsDesktopViewport(event.matches);
    };

    setIsDesktopViewport(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (isDesktopViewport) {
      setMobileSidebarOpen(false);
    }
  }, [isDesktopViewport]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    document.body.style.overflow = !isDesktopViewport && mobileSidebarOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isDesktopViewport, mobileSidebarOpen]);

  const closeSidebar = () => {
    if (!isDesktopViewport) {
      setMobileSidebarOpen(false);
    }
  };
  const openSidebar = () => {
    if (isDesktopViewport) {
      setDesktopSidebarExpanded(true);
      return;
    }

    setMobileSidebarOpen(true);
  };
  const toggleSidebar = () => {
    if (isDesktopViewport) {
      setDesktopSidebarExpanded((current) => !current);
      return;
    }

    setMobileSidebarOpen((current) => !current);
  };
  const handleNavigate = () => {
    if (!isDesktopViewport) {
      setMobileSidebarOpen(false);
    }
  };
  const shellClassName = cx(
    "app-shell",
    isDesktopViewport ? "is-desktop" : "is-mobile",
    isDesktopViewport
      ? desktopSidebarExpanded
        ? "sidebar-expanded"
        : "sidebar-collapsed"
      : mobileSidebarOpen
        ? "sidebar-mobile-open"
        : "sidebar-mobile-closed"
  );

  return (
    <div className={shellClassName}>
      <Sidebar
        isDesktopViewport={isDesktopViewport}
        isExpanded={desktopSidebarExpanded}
        isOpen={isDesktopViewport ? true : mobileSidebarOpen}
        onClose={closeSidebar}
        onNavigate={handleNavigate}
        onToggle={toggleSidebar}
      />
      {!isDesktopViewport && !mobileSidebarOpen ? (
        <SidebarRevealButton onToggle={openSidebar} />
      ) : null}

      <div className="main-shell">
        <Topbar />

        <main className="content-shell">
          {status === "loading" && !hydrated ? <WorkspaceLoading /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
